import React, { useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faArrowRight, faClock, faListOl, faCircleCheck, faBookOpen,
} from '@fortawesome/free-solid-svg-icons';
import SEO from '../components/SEO';
import LocalizedLink from '../components/LocalizedLink';
import { getCourseBySlug, getCurriculum } from '../data/courseLessons';

const levelLabelKey = {
  beginner: 'learn.level.beginner',
  intermediate: 'learn.level.intermediate',
  advanced: 'learn.level.advanced',
} as const;

const CoursePage: React.FC = () => {
  const { courseSlug } = useParams<{ courseSlug: string }>();
  const { t } = useTranslation();

  const course = courseSlug ? getCourseBySlug(courseSlug) : undefined;
  const curriculum = course ? getCurriculum(course.id) : undefined;

  const totalMinutes = useMemo(
    () => curriculum?.lessons.reduce((sum, l) => sum + l.durationMinutes, 0) || 0,
    [curriculum]
  );

  if (!course || course.status !== 'available' || !curriculum) {
    return <Navigate to="/learn" replace />;
  }

  const title = t(course.titleKey);
  const description = t(course.descriptionKey);
  const firstLesson = curriculum.lessons[0];

  return (
    <>
      <SEO
        title={`${title} | GlobalPreps`}
        description={curriculum.overview || description}
        keywords={`${title}, AI course, Gen AI, ${course.topics.join(', ')}`}
        url={`/learn/${course.slug}`}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: title,
          description: curriculum.overview,
          provider: { '@type': 'Organization', name: 'GlobalPreps', url: 'https://globalpreps.com' },
          educationalLevel: course.level,
          timeRequired: `PT${course.durationHours}H`,
          numberOfCredits: curriculum.lessons.length,
          hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: 'online',
            courseWorkload: `PT${totalMinutes}M`,
          },
        }}
        breadcrumbs={[
          { name: t('nav.home'), url: '/' },
          { name: t('nav.learn'), url: '/learn' },
          { name: title, url: `/learn/${course.slug}` },
        ]}
      />

      <div className="gp-page">
        <div className="gp-container pt-6 sm:pt-8 pb-10">
          <LocalizedLink to="/learn" className="gp-back-link mb-4">
            <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
            {t('learn.backToLearn')}
          </LocalizedLink>

          <header className="mb-6 max-w-3xl">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="gp-badge gp-badge-ai">{t('learn.status.available')}</span>
              <span className="text-xs font-semibold" style={{ color: 'var(--gp-brand)' }}>
                {t(levelLabelKey[course.level])}
              </span>
              <span className="text-xs gp-text-muted flex items-center gap-1">
                <FontAwesomeIcon icon={faClock} aria-hidden="true" />
                {t('learn.durationHours', { count: course.durationHours })} · {curriculum.lessons.length}{' '}
                {t('learn.lessonsLabel')}
              </span>
            </div>
            <h1 className="gp-heading-xl mb-2">{title}</h1>
            <p className="text-sm sm:text-base gp-text-muted leading-relaxed">{curriculum.overview}</p>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {course.topics.map((topic) => (
                <span
                  key={topic}
                  className="text-[11px] px-1.5 py-0.5 rounded font-medium"
                  style={{ background: 'var(--gp-brand-soft)', color: 'var(--gp-brand)' }}
                >
                  {topic}
                </span>
              ))}
            </div>
            {firstLesson && (
              <LocalizedLink
                to={`/learn/${course.slug}/${firstLesson.slug}`}
                className="gp-btn-primary mt-5 inline-flex"
              >
                {t('learn.startCourse')}
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </LocalizedLink>
            )}
          </header>

          <div className="grid lg:grid-cols-12 gap-5">
            <section className="lg:col-span-4" aria-labelledby="outcomes-heading">
              <div className="gp-card">
                <h2 id="outcomes-heading" className="font-display text-base font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
                  <FontAwesomeIcon icon={faCircleCheck} style={{ color: 'var(--gp-success)' }} aria-hidden="true" />
                  {t('learn.whatYouLearn')}
                </h2>
                <ul className="space-y-2 text-sm gp-text-muted list-none p-0 m-0">
                  {curriculum.outcomes.map((outcome) => (
                    <li key={outcome} className="flex gap-2">
                      <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 text-xs shrink-0" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                      <span>{outcome}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="lg:col-span-8" aria-labelledby="curriculum-heading">
              <div className="flex items-center gap-2 mb-3">
                <FontAwesomeIcon icon={faListOl} style={{ color: 'var(--gp-ai)' }} aria-hidden="true" />
                <h2 id="curriculum-heading" className="gp-heading-lg">{t('learn.curriculum')}</h2>
              </div>
              <ol className="space-y-2 list-none p-0 m-0">
                {curriculum.lessons.map((lesson, index) => (
                  <li key={lesson.id}>
                    <LocalizedLink
                      to={`/learn/${course.slug}/${lesson.slug}`}
                      className="gp-card gp-card-interactive flex gap-3 items-start no-underline"
                    >
                      <span
                        className="w-8 h-8 rounded-md shrink-0 flex items-center justify-center text-xs font-bold text-white"
                        style={{ background: 'var(--gp-brand)' }}
                        aria-hidden="true"
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-sm mb-0.5" style={{ color: 'var(--gp-ink)' }}>
                          {lesson.title}
                        </h3>
                        <p className="text-xs gp-text-muted mb-1.5">{lesson.summary}</p>
                        <span className="text-[11px] gp-text-muted inline-flex items-center gap-1">
                          <FontAwesomeIcon icon={faBookOpen} aria-hidden="true" />
                          {t('learn.readMinutes', { count: lesson.durationMinutes })}
                        </span>
                      </div>
                      <FontAwesomeIcon icon={faArrowRight} className="text-xs mt-1" style={{ color: 'var(--gp-brand)' }} aria-hidden="true" />
                    </LocalizedLink>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoursePage;
